/**
 * PermissionListPage — /permissions
 *
 * 5 상태:
 *  1. loading  — Skeleton rows
 *  2. error    — ErrorState + 재시도
 *  3. empty    — EmptyState + 권한 추가 버튼 (ROLE_WRITE 필요)
 *  4. default  — DataTable (code/resource/action/nameKo/description)
 *  5. dialog   — PermissionFormDialog (create/edit) or PermissionDeleteConfirm
 *
 * RBAC:
 *  - ButtonGuard(requirePermission="ROLE_WRITE") controls create/edit/delete buttons.
 *  - ROLE_EXEC (read-only) sees buttons disabled with reason tooltip.
 */
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader, DataTable, EmptyState, ErrorState, StatusPill } from '@ui-kit';
import type { DataTableColumn } from '@ui-kit';
import { usePermissionList, useCreatePermission, useUpdatePermission, useDeletePermission } from '../hooks/usePermissions';
import { PermissionFormDialog } from '../components/PermissionFormDialog';
import { PermissionDeleteConfirm } from '../components/PermissionDeleteConfirm';
import { ButtonGuard } from '../../_shared/components/ButtonGuard';
import type { PermissionItem, ApiError } from '../types/role';
import type { PermissionCreateFormValues } from '../schema/permissionSchema';

// ─────────────────────────────────────────────────────────────────────────────
// Skeleton row — 5 rows during loading
// ─────────────────────────────────────────────────────────────────────────────
function SkeletonRow() {
  return (
    <tr>
      {[40, 20, 20, 30, 60, 80].map((w, i) => (
        <td
          key={i}
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            borderBottom: '1px solid var(--color-surface-border)',
          }}
        >
          <div
            style={{
              height: 14,
              width: `${w}%`,
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-surface-elevated)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        </td>
      ))}
    </tr>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────
export function PermissionListPage() {
  const { t } = useTranslation();

  // ── filter state ──
  const [resourceFilter, setResourceFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [appliedFilters, setAppliedFilters] = useState<{ resource?: string; action?: string }>({});

  // ── dialog state ──
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<PermissionItem | undefined>();
  const [formError, setFormError] = useState<string | undefined>();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PermissionItem | undefined>();
  const [deleteError, setDeleteError] = useState<string | undefined>();
  const [deleteErrorCode, setDeleteErrorCode] = useState<string | undefined>();

  // ── toast ──
  const [toastMessage, setToastMessage] = useState<string | undefined>();

  const showToast = useCallback((msg: string) => {
    setToastMessage(msg);
    const timer = setTimeout(() => setToastMessage(undefined), 4000);
    return () => clearTimeout(timer);
  }, []);

  // ── queries / mutations ──
  const listQuery = usePermissionList(appliedFilters);
  const createMutation = useCreatePermission();

  // For update/delete, IDs are determined at call time:
  const [activeEditId, setActiveEditId] = useState<number | undefined>();
  const [activeDeleteId, setActiveDeleteId] = useState<number | undefined>();

  const updateMutation = useUpdatePermission(activeEditId ?? 0);
  const deleteMutation = useDeletePermission();

  const items = listQuery.data ?? [];
  const isListLoading = listQuery.isLoading;
  const isListError = listQuery.isError;
  const isEmpty = !isListLoading && !isListError && items.length === 0;
  const showTable = !isListLoading && !isListError && items.length > 0;

  // ── handlers ──
  const handleSearch = useCallback(() => {
    const f: { resource?: string; action?: string } = {};
    if (resourceFilter.trim()) f.resource = resourceFilter.trim();
    if (actionFilter.trim()) f.action = actionFilter.trim();
    setAppliedFilters(f);
  }, [resourceFilter, actionFilter]);

  const handleReset = useCallback(() => {
    setResourceFilter('');
    setActionFilter('');
    setAppliedFilters({});
  }, []);

  const openCreateDialog = useCallback(() => {
    setEditItem(undefined);
    setActiveEditId(undefined);
    setFormError(undefined);
    setFormDialogOpen(true);
  }, []);

  const openEditDialog = useCallback((item: PermissionItem) => {
    setEditItem(item);
    setActiveEditId(item.id);
    setFormError(undefined);
    setFormDialogOpen(true);
  }, []);

  const openDeleteDialog = useCallback((item: PermissionItem) => {
    setDeleteTarget(item);
    setActiveDeleteId(item.id);
    setDeleteError(undefined);
    setDeleteErrorCode(undefined);
    setDeleteDialogOpen(true);
  }, []);

  const handleFormSubmit = useCallback(
    (values: PermissionCreateFormValues) => {
      setFormError(undefined);
      if (editItem && activeEditId) {
        // PATCH — code not sent (server ignores it; only send editable fields)
        updateMutation.mutate(
          {
            nameKo: values.nameKo,
            resource: values.resource,
            action: values.action,
            description: values.description,
          },
          {
            onSuccess: () => {
              setFormDialogOpen(false);
              showToast(t('permission.toast.updated'));
            },
            onError: (err: unknown) => {
              const apiErr = err as ApiError;
              setFormError(apiErr.message ?? t('permission.error.network'));
            },
          },
        );
      } else {
        createMutation.mutate(
          {
            code: values.code,
            resource: values.resource,
            action: values.action,
            nameKo: values.nameKo,
            description: values.description,
          },
          {
            onSuccess: () => {
              setFormDialogOpen(false);
              showToast(t('permission.toast.created'));
            },
            onError: (err: unknown) => {
              const apiErr = err as ApiError;
              setFormError(apiErr.message ?? t('permission.error.network'));
            },
          },
        );
      }
    },
    [editItem, activeEditId, createMutation, updateMutation, t, showToast],
  );

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteTarget || activeDeleteId === undefined) return;
    setDeleteError(undefined);
    setDeleteErrorCode(undefined);
    deleteMutation.mutate(
      { id: activeDeleteId, force: false },
      {
        onSuccess: () => {
          setDeleteDialogOpen(false);
          showToast(t('permission.toast.deleted'));
        },
        onError: (err: unknown) => {
          const apiErr = err as ApiError;
          setDeleteError(apiErr.message ?? t('permission.error.network'));
          setDeleteErrorCode(apiErr.code);
        },
      },
    );
  }, [deleteTarget, activeDeleteId, deleteMutation, t, showToast]);

  const handleForceDelete = useCallback(() => {
    if (!deleteTarget || activeDeleteId === undefined) return;
    setDeleteError(undefined);
    setDeleteErrorCode(undefined);
    deleteMutation.mutate(
      { id: activeDeleteId, force: true },
      {
        onSuccess: () => {
          setDeleteDialogOpen(false);
          showToast(t('permission.toast.deleted'));
        },
        onError: (err: unknown) => {
          const apiErr = err as ApiError;
          setDeleteError(apiErr.message ?? t('permission.error.network'));
          setDeleteErrorCode(apiErr.code);
        },
      },
    );
  }, [deleteTarget, activeDeleteId, deleteMutation, t, showToast]);

  // ── columns ──
  const columns: DataTableColumn<PermissionItem>[] = [
    {
      key: 'code',
      header: t('permission.list.column.code'),
      render: (row) => (
        <span
          style={{
            fontFamily: 'var(--font-family-mono)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-primary)',
          }}
        >
          {row.code}
        </span>
      ),
    },
    {
      key: 'resource',
      header: t('permission.list.column.resource'),
      render: (row) => (
        <StatusPill tone="info">{row.resource}</StatusPill>
      ),
    },
    {
      key: 'action',
      header: t('permission.list.column.action'),
      render: (row) => (
        <StatusPill
          tone={
            row.action === 'ADMIN'
              ? 'warning'
              : row.action === 'WRITE'
              ? 'success'
              : 'neutral'
          }
        >
          {row.action}
        </StatusPill>
      ),
    },
    {
      key: 'nameKo',
      header: t('permission.list.column.nameKo'),
      render: (row) => (
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>
          {row.nameKo}
        </span>
      ),
    },
    {
      key: 'description',
      header: t('permission.list.column.description'),
      render: (row) => (
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-muted)',
            maxWidth: 260,
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={row.description ?? ''}
        >
          {row.description || '-'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t('permission.list.column.actions'),
      width: '120px',
      render: (row) => (
        <div style={{ display: 'flex', gap: 'var(--spacing-1)' }}>
          {/* 편집 버튼 */}
          <ButtonGuard requirePermission="ROLE_WRITE">
            {({ disabled, reason }) => (
              <button
                type="button"
                aria-label={`${row.nameKo} ${t('permission.list.action.edit')}`}
                disabled={disabled}
                title={disabled ? reason : undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!disabled) openEditDialog(row);
                }}
                style={{
                  background: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '2px var(--spacing-2)',
                  fontSize: 'var(--font-size-xs)',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  color: disabled ? 'var(--color-text-muted)' : 'var(--color-text-secondary)',
                  opacity: disabled ? 0.5 : 1,
                }}
              >
                {t('permission.list.action.edit')}
              </button>
            )}
          </ButtonGuard>

          {/* 삭제 버튼 */}
          <ButtonGuard requirePermission="ROLE_WRITE">
            {({ disabled, reason }) => (
              <button
                type="button"
                aria-label={`${row.nameKo} ${t('permission.list.action.delete')}`}
                disabled={disabled}
                title={disabled ? reason : undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!disabled) openDeleteDialog(row);
                }}
                style={{
                  background: 'transparent',
                  border: `1px solid ${disabled ? 'var(--color-surface-border)' : 'var(--color-danger)'}`,
                  borderRadius: 'var(--radius-sm)',
                  padding: '2px var(--spacing-2)',
                  fontSize: 'var(--font-size-xs)',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  color: disabled ? 'var(--color-text-muted)' : 'var(--color-danger)',
                  opacity: disabled ? 0.5 : 1,
                }}
              >
                {t('permission.list.action.delete')}
              </button>
            )}
          </ButtonGuard>
        </div>
      ),
    },
  ];

  // ── shared styles ──
  const btnPrimary: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    padding: 'var(--spacing-2) var(--spacing-4)',
    background: 'var(--color-brand-primary)',
    color: 'var(--color-text-inverse)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-family-sans)',
  };

  const btnSecondary: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    padding: 'var(--spacing-2) var(--spacing-4)',
    background: 'var(--color-surface-elevated)',
    color: 'var(--color-text-primary)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-family-sans)',
  };

  const inputStyle: React.CSSProperties = {
    padding: 'var(--spacing-2) var(--spacing-3)',
    background: 'var(--color-surface-elevated)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-family-sans)',
    outline: 'none',
  };

  return (
    <div style={{ padding: 'var(--spacing-6)' }}>
      {/* 페이지 헤더 */}
      <PageHeader
        title={t('permission.list.title')}
        subtitle={t('permission.list.pageHeader.subtitle')}
        breadcrumb={<span>{t('permission.list.breadcrumb')}</span>}
        actions={
          <ButtonGuard requirePermission="ROLE_WRITE">
            {({ disabled, reason }) => (
              <button
                type="button"
                onClick={() => {
                  if (!disabled) openCreateDialog();
                }}
                disabled={disabled}
                title={disabled ? reason : undefined}
                aria-label={t('permission.list.button.create')}
                aria-disabled={disabled}
                style={{
                  ...btnPrimary,
                  ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                }}
              >
                {t('permission.list.button.create')}
              </button>
            )}
          </ButtonGuard>
        }
      />

      {/* 성공 토스트 */}
      {toastMessage && (
        <div
          role="status"
          aria-live="polite"
          style={{
            marginBottom: 'var(--spacing-4)',
            padding: 'var(--spacing-3) var(--spacing-4)',
            background:
              'color-mix(in srgb, var(--color-success) 12%, var(--color-surface-muted))',
            border: '1px solid var(--color-success)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-success)',
          }}
        >
          {toastMessage}
        </div>
      )}

      {/* 데이터 요약 */}
      {!isListLoading && !isListError && (
        <p
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            margin: 'var(--spacing-2) 0 var(--spacing-4)',
          }}
        >
          {t('permission.list.subtitle', { total: items.length })}
        </p>
      )}

      {/* 검색/필터 바 */}
      <div
        role="search"
        aria-label={t('permission.list.search.ariaLabel')}
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr auto',
          gap: 'var(--spacing-3)',
          padding: 'var(--spacing-4)',
          background: 'var(--color-surface-muted)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--spacing-4)',
          alignItems: 'end',
        }}
      >
        {/* 리소스 필터 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
          <label
            htmlFor="perm-filter-resource"
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-secondary)',
              fontWeight: 500,
            }}
          >
            {t('permission.list.filter.resource')}
          </label>
          <input
            id="perm-filter-resource"
            type="text"
            placeholder={t('permission.list.filter.placeholder.resource')}
            value={resourceFilter}
            onChange={(e) => setResourceFilter(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            style={inputStyle}
          />
        </div>

        {/* 액션 필터 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
          <label
            htmlFor="perm-filter-action"
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-secondary)',
              fontWeight: 500,
            }}
          >
            {t('permission.list.filter.action')}
          </label>
          <input
            id="perm-filter-action"
            type="text"
            placeholder={t('permission.list.filter.placeholder.action')}
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            style={inputStyle}
          />
        </div>

        {/* 버튼 그룹 */}
        <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
          <button type="button" onClick={handleSearch} style={btnPrimary}>
            {t('permission.list.filter.search')}
          </button>
          <button type="button" onClick={handleReset} style={btnSecondary}>
            {t('permission.list.filter.reset')}
          </button>
        </div>
      </div>

      {/* 컨텐츠 영역 — 5상태 */}
      <div
        style={{
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          background: 'var(--color-surface-background)',
        }}
      >
        {/* State 1: loading — skeleton */}
        {isListLoading && (
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 'var(--font-size-sm)',
            }}
            aria-label={t('permission.list.loading.ariaLabel')}
            aria-busy="true"
          >
            <thead>
              <tr style={{ background: 'var(--color-surface-muted)' }}>
                {[
                  t('permission.list.column.code'),
                  t('permission.list.column.resource'),
                  t('permission.list.column.action'),
                  t('permission.list.column.nameKo'),
                  t('permission.list.column.description'),
                  '',
                ].map((h, i) => (
                  <th
                    key={i}
                    style={{
                      textAlign: 'left',
                      padding: 'var(--spacing-3) var(--spacing-4)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 600,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => (
                <SkeletonRow key={i} />
              ))}
            </tbody>
          </table>
        )}

        {/* State 2: error */}
        {isListError && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <ErrorState
              title={t('permission.error.loadFailed')}
              message={
                (listQuery.error as unknown as ApiError)?.code === 'AUTH-403-001'
                  ? t('permission.error.AUTH-403-001')
                  : ((listQuery.error as unknown as ApiError)?.message ??
                    t('permission.error.network'))
              }
              onRetry={() => void listQuery.refetch()}
            />
          </div>
        )}

        {/* State 3: empty */}
        {isEmpty && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <EmptyState
              title={t('permission.list.empty.title')}
              description={t('permission.list.empty.description')}
              action={
                <ButtonGuard requirePermission="ROLE_WRITE">
                  {({ disabled, reason }) => (
                    <button
                      type="button"
                      onClick={() => {
                        if (!disabled) openCreateDialog();
                      }}
                      disabled={disabled}
                      title={disabled ? reason : undefined}
                      style={{
                        ...btnPrimary,
                        ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                      }}
                    >
                      {t('permission.list.button.create')}
                    </button>
                  )}
                </ButtonGuard>
              }
            />
          </div>
        )}

        {/* State 4: default — DataTable */}
        {showTable && (
          <div
            role="region"
            aria-label={t('permission.list.region.ariaLabel')}
            style={{ overflowX: 'auto' }}
          >
            <DataTable
              columns={columns}
              rows={items}
              rowKey={(row) => row.id ?? row.code}
            />
          </div>
        )}
      </div>

      {/* State 5 (dialog): PermissionFormDialog — create/edit */}
      <PermissionFormDialog
        open={formDialogOpen}
        editItem={editItem}
        onClose={() => setFormDialogOpen(false)}
        onSubmit={handleFormSubmit}
        isLoading={createMutation.isPending || updateMutation.isPending}
        errorMessage={formError}
      />

      {/* State 5 (dialog): PermissionDeleteConfirm — delete + force */}
      <PermissionDeleteConfirm
        open={deleteDialogOpen}
        permissionCode={deleteTarget?.code ?? ''}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        onForceDelete={handleForceDelete}
        isLoading={deleteMutation.isPending}
        errorMessage={deleteError}
        errorCode={deleteErrorCode}
      />
    </div>
  );
}
